import api from './api';

// ProtoJSON encodes int64/uint64 as strings to retain exact IDs and ordinals.
export type WireInt = number | string;
export interface InspectionUser {
  userId: string;
  displayName: string;
  avatarUrl: string;
}
export interface InspectionRoom {
  roomId: string;
  name: string;
  owner: InspectionUser;
  createdAtUnixMs: WireInt;
  updatedAtUnixMs: WireInt;
}
export interface InspectionMember {
  user: InspectionUser;
  role: string;
  left: boolean;
  sessionCount: number;
  privateSessionCount: number;
}
export interface UserOverview {
  user: InspectionUser;
  roomCount: number;
  roomCountCapped: boolean;
  sessionCount: number;
  privateSessionCount: number;
}
export interface UserRoom {
  room: InspectionRoom;
  role: string;
  left: boolean;
  joinedAtUnixMs: WireInt;
  sessionCount: number;
  privateSessionCount: number;
}
export interface RoomOverview {
  room: InspectionRoom;
  members: InspectionMember[];
  sessionCount: number;
  privateSessionCount: number;
  artifactCount: number;
}
export interface InspectionSession {
  agentSessionId: string;
  agentTargetId?: string;
  title: string;
  createdByUserId: string;
  provider: string;
  status: string;
  accessMode: string;
  railSectionKey: string;
  createdAtUnixMs: WireInt;
  lastEventAtUnixMs: WireInt;
  activeTurnId?: string;
  activeTurnPhase?: string;
  imported?: boolean;
  settings?: { model?: string };
  agentTargetPresentation?: {
    provider?: string;
    displayName?: string;
    iconKey?: string;
    ownerUserId?: string;
    ownerDisplayName?: string;
    ownerAvatarUrl?: string;
    computerDisplayName?: string;
    ownership?: string;
  };
}
export interface ChildSession {
  agentSessionId: string;
  parentAgentSessionId: string;
  title: string;
  provider: string;
  model: string;
}
export interface RailSection {
  railSectionKey: string;
  sessions: InspectionSession[];
  nextCursor: string;
}
export interface InspectionRail {
  projects: {
    projectId: string;
    label: string;
    path: string;
    sectionKey: string;
  }[];
  pinned: RailSection;
  sections: RailSection[];
  nextProjectsCursor: string;
}
export interface InspectionMessage {
  messageId: string;
  ordinal: WireInt;
  turnId: string;
  role: string;
  kind: string;
  status: string;
  actorUserId: string;
  payload: unknown;
  semantics: unknown;
  occurredAtUnixMs: WireInt;
}
export interface InspectionTurn {
  turnId: string;
  phase: string;
  outcome: string;
  deliverResult: unknown;
  error?: unknown;
  completedCommand?: {
    finalAssistantMessageId?: string;
    finalAssistantMessageResolved?: boolean;
  } | null;
}
export interface InspectionSnapshot {
  session: InspectionSession;
  messages: InspectionMessage[];
  turns: InspectionTurn[];
  historyNextCursor: string;
  childSessions: ChildSession[];
  latestTurnInteractions: {
    requestId: string;
    turnId?: string;
    kind: string;
    status: string;
    toolName: string;
    input: unknown;
    output: unknown;
  }[];
}
export interface InspectionArtifact {
  artifactId: string;
  artifactType: string;
  displayName: string;
  description: string;
  fileName: string;
  mimeType: string;
  sizeBytes: WireInt;
  coverUrl: string;
  sourceAgentSessionId: string;
  sourceAgentSessionTitle: string;
  owner: InspectionUser;
  updatedAtUnixMs: WireInt;
}
export interface ArtifactPreview {
  artifactId: string;
  artifactType: string;
  url: string;
  fileName: string;
  mimeType: string;
  expiresAtUnixMs: WireInt;
  websiteShareUrl: string;
  websiteShareVisibility: string;
  websiteShareStatus: string;
}
type Params = Record<string, string | number | boolean | undefined>;
// The gateway maps the shared /api/admin prefix to the BFF service root.
const base = '/api/v1/admin/room-inspector';
const room = (id: string) => `${base}/rooms/${encodeURIComponent(id)}`;
const session = (roomId: string, id: string) =>
  `${room(roomId)}/sessions/${encodeURIComponent(id)}`;
export const roomInspectorApi = {
  user: (id: string) =>
    api.get<never, UserOverview>(`${base}/users/${encodeURIComponent(id)}`),
  userRooms: (id: string, params: Params) =>
    api.get<never, { rooms: UserRoom[]; nextCursor: string }>(
      `${base}/users/${encodeURIComponent(id)}/rooms`,
      { params },
    ),
  room: (id: string) => api.get<never, RoomOverview>(room(id)),
  rail: (id: string, creatorUserId?: string, projectsCursor?: string) =>
    api.get<never, InspectionRail>(`${room(id)}/rail`, {
      params: { creatorUserId, projectsCursor, limitPerSection: 20 },
    }),
  sessions: (id: string, params: Params) =>
    api.get<never, { sessions: InspectionSession[]; nextCursor: string }>(
      `${room(id)}/sessions`,
      { params },
    ),
  snapshot: (roomId: string, id: string) =>
    api.get<never, { snapshot: InspectionSnapshot }>(
      `${session(roomId, id)}/snapshot`,
      { params: { pageSize: 50 } },
    ),
  messages: (roomId: string, id: string, cursor: string) =>
    api.get<
      never,
      {
        history: {
          items: InspectionMessage[];
          turns: InspectionTurn[];
          nextCursor: string;
        };
      }
    >(`${session(roomId, id)}/messages`, { params: { cursor, pageSize: 50 } }),
  artifacts: (id: string, params: Params) =>
    api.get<never, { artifacts: InspectionArtifact[]; nextCursor: string }>(
      `${room(id)}/artifacts`,
      { params },
    ),
  preview: (roomId: string, id: string) =>
    api.post<never, ArtifactPreview>(
      `${room(roomId)}/artifacts/${encodeURIComponent(id)}/preview-url`,
      {},
    ),
  fileContent: (roomId: string, id: string) =>
    api.get<never, Blob>(
      `${room(roomId)}/artifacts/${encodeURIComponent(id)}/preview-content`,
      {
        responseType: 'blob',
      },
    ),
};
